import React, { useEffect, useState } from "react";
import { CircularProgress, makeStyles } from "@material-ui/core";
import InsertDriveFileOutlinedIcon from "@material-ui/icons/InsertDriveFileOutlined";
import GetAppIcon from "@material-ui/icons/GetApp";
import ModalImage from "react-modal-image";

import api from "../../services/api";

// Os anexos ficam numa rota autenticada: baixa com o token e guarda o blob
// por mensagem, para não baixar de novo a cada renderização.
const blobCache = new Map();

const loadBlobUrl = (url) => {
  if (!blobCache.has(url)) {
    const promise = api
      .get(url, { responseType: "blob" })
      .then(({ data, headers }) =>
        window.URL.createObjectURL(
          new Blob([data], { type: headers["content-type"] })
        )
      )
      .catch((err) => {
        blobCache.delete(url);
        throw err;
      });
    blobCache.set(url, promise);
  }
  return blobCache.get(url);
};

const useStyles = makeStyles((theme) => {
  const isDark = theme.palette.type === "dark";
  return {
    image: {
      display: "block",
      maxWidth: "100%",
      width: 260,
      maxHeight: 260,
      objectFit: "cover",
      borderRadius: 8,
      cursor: "zoom-in",
    },
    placeholder: {
      width: 260,
      maxWidth: "100%",
      height: 160,
      borderRadius: 8,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: isDark ? "rgba(255,255,255,0.06)" : "rgba(16,24,40,0.06)",
    },
    video: {
      display: "block",
      width: 280,
      maxWidth: "100%",
      borderRadius: 8,
    },
    audio: { width: 260, maxWidth: "100%", display: "block" },
    file: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      padding: "8px 10px",
      minWidth: 220,
      maxWidth: "100%",
      boxSizing: "border-box",
      borderRadius: 8,
      border: 0,
      cursor: "pointer",
      fontFamily: "inherit",
      textAlign: "left",
      color: "inherit",
      backgroundColor: isDark ? "rgba(255,255,255,0.08)" : "rgba(16,24,40,0.05)",
    },
    fileOwn: { backgroundColor: "rgba(255,255,255,0.16)" },
    fileName: {
      flex: 1,
      minWidth: 0,
      fontSize: 13,
      fontWeight: 600,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    error: { fontSize: 12, opacity: 0.8 },
  };
});

export default function ChatMedia({ message, own }) {
  const classes = useStyles();
  const [url, setUrl] = useState(null);
  const [failed, setFailed] = useState(false);
  const { mediaUrl, mediaType, mediaName } = message;
  const isDocument = !["image", "video", "audio"].includes(mediaType);

  useEffect(() => {
    let active = true;
    // documento só baixa ao clicar
    if (!mediaUrl || isDocument) return undefined;
    loadBlobUrl(mediaUrl)
      .then((u) => active && setUrl(u))
      .catch(() => active && setFailed(true));
    return () => {
      active = false;
    };
  }, [mediaUrl, isDocument]);

  const download = async () => {
    try {
      const u = await loadBlobUrl(mediaUrl);
      const a = document.createElement("a");
      a.href = u;
      a.download = mediaName || "arquivo";
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (err) {
      setFailed(true);
    }
  };

  if (failed) {
    return <span className={classes.error}>Não foi possível carregar o anexo.</span>;
  }

  if (isDocument) {
    return (
      <button
        type="button"
        onClick={download}
        className={`${classes.file} ${own ? classes.fileOwn : ""}`}
        aria-label={`Baixar ${mediaName || "arquivo"}`}
      >
        <InsertDriveFileOutlinedIcon />
        <span className={classes.fileName}>{mediaName || "Arquivo"}</span>
        <GetAppIcon fontSize="small" />
      </button>
    );
  }

  if (!url) {
    return (
      <div className={classes.placeholder} aria-label="Carregando anexo">
        <CircularProgress size={20} color="inherit" />
      </div>
    );
  }

  if (mediaType === "image") {
    return (
      <ModalImage
        className={classes.image}
        small={url}
        medium={url}
        large={url}
        alt={mediaName || "Imagem"}
        hideDownload={false}
      />
    );
  }

  if (mediaType === "video") {
    // eslint-disable-next-line jsx-a11y/media-has-caption
    return <video className={classes.video} src={url} controls preload="metadata" />;
  }

  // eslint-disable-next-line jsx-a11y/media-has-caption
  return <audio className={classes.audio} src={url} controls preload="metadata" />;
}
